import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/common/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, type SelectOption } from '../../components/ui/select';
import { partnerApi, type PartnerUser } from '../../lib/api';

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<{
    [page: number]: string | undefined;
  }>({});

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Fetch tenants for the filter dropdown
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => partnerApi.getTenants(),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'partner-users',
      debouncedSearchTerm,
      statusFilter,
      tenantFilter,
      currentPage,
      pageCursors[currentPage],
    ],
    queryFn: () =>
      partnerApi.getPartnerUsers({
        limit: 20,
        cursor: pageCursors[currentPage],
        search: debouncedSearchTerm || undefined,
        status: statusFilter || undefined,
        tenantId: tenantFilter || undefined,
      }),
    staleTime: 30000,
    gcTime: 300000,
  });

  useEffect(() => {
    if (data?.data?.pagination.cursor) {
      setPageCursors(prev => ({
        ...prev,
        [currentPage + 1]: data.data.pagination.cursor || undefined,
      }));
    }
  }, [data, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    setPageCursors({});
  }, [debouncedSearchTerm, statusFilter, tenantFilter]);

  const users: PartnerUser[] = data?.data?.results || [];
  const pagination = data?.data?.pagination;

  // Create tenant options for the select component
  const tenantOptions: SelectOption[] = [
    { value: '', label: 'All Tenants' },
    ...(tenantsData?.data?.results?.map(tenant => ({
      value: tenant.id,
      label: tenant.name,
    })) || []),
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="Manage and view users across your tenants with filtering options."
      />

      <div className="flex flex-col sm:flex-row gap-4">
        <Input
          type="text"
          placeholder="Search by name, email, or external ID..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="sm:flex-1"
        />
        <Select
          options={[
            { value: '', label: 'All Statuses' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="All Statuses"
          className="min-w-[140px]"
        />
        <Select
          options={tenantOptions}
          value={tenantFilter}
          onChange={setTenantFilter}
          placeholder="All Tenants"
          searchable={true}
          className="min-w-[180px]"
        />
      </div>

      {error ? (
        <div className="bg-red-50 p-4 rounded-lg text-red-700">
          Failed to load users.
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-600">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading users...
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-gray-600">No users found.</div>
      ) : (
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    External ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map(u => (
                  <tr
                    key={u.id}
                    onClick={() =>
                      navigate(
                        `/dashboard/users/${u.externalId}?tenantId=${u.tenantId}`,
                      )
                    }
                    className="hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {u.firstName && u.lastName
                            ? `${u.firstName} ${u.lastName}`
                            : u.firstName || u.lastName || 'Unnamed User'}
                        </span>
                        {u.status === 'deactivated' && (
                          <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20">
                            Deactivated
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {u.tenantName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {u.externalId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {u.email || (
                        <span className="text-gray-400">No email</span>
                      )}
                      {u.phone && (
                        <div className="text-gray-500">{u.phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(u.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {(() => {
                  const startItem = (currentPage - 1) * 20 + 1;
                  const endItem = startItem + users.length - 1;
                  const total = pagination?.total || 0;
                  if (total === 0 && !isLoading) return 'No users found';
                  if (users.length === 0) return 'Loading...';
                  return `Viewing ${startItem}-${endItem} of ${total} total users`;
                })()}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || isLoading}>
                  Previous
                </Button>
                <span className="text-sm text-gray-700">
                  Page {currentPage}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage(p => (pagination?.hasNextPage ? p + 1 : p))
                  }
                  disabled={!pagination?.hasNextPage || isLoading}>
                  {isLoading ? 'Loading…' : 'Next'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
